import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  DrawingToolsContainer,
  DrawingToolsFAB,
  DrawingToolsPanel,
  DrawingToolButton,
  DrawToolsDivider,
} from './DrawingTools.styles'
import { DrawingToolsProps, DrawingToolOption } from './DrawingTools.types'

const useDefaultTools = (): DrawingToolOption[] => {
  const { t } = useTranslation('ui')

  return [
    {
      id: 'line',
      label: t('drawingTools.drawLine'),
      icon: (
        <svg className="icon" viewBox="0 0 24 24">
          <path d="M4 20L20 4" />
        </svg>
      ),
    },
    {
      id: 'polygon',
      label: t('drawingTools.drawPolygon'),
      icon: (
        <svg className="icon" viewBox="0 0 24 24">
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
        </svg>
      ),
    },
    {
      id: '3d',
      label: t('drawingTools.3dMode'),
      icon: (
        <svg className="icon" viewBox="0 0 24 24">
          <path d="M12 2L2 9v11l10 7 10-7V9L12 2z" />
          <path d="M12 22V12" />
          <path d="M12 12L2 9" />
          <path d="M12 12l10-3" />
        </svg>
      ),
    },
  ]
}

export const DrawingTools: React.FC<DrawingToolsProps> = ({
  open,
  onToggle,
  activeTool = 'none',
  onToolSelect,
  onDeleteAll,
  tools,
  showDeleteAll = true,
  deleteButtonLabel,
  className,
  fabPosition,
  zIndex,
  fabContent = (
    <svg className="icon" viewBox="0 0 24 24">
      <path
        d="M13.5 6.5L17.5 10.5M4 20h4L18.5 9.5a2.828 2.828 0 10-4-4L4 16v4z"
        stroke="currentColor"
        fill="none"
        strokeWidth="2"
      />
    </svg>
  ),
  fabTooltip,
}) => {
  const { t } = useTranslation('ui')
  const defaultTools = useDefaultTools()
  const resolvedTools = tools ?? defaultTools
  const resolvedDeleteLabel = deleteButtonLabel ?? t('drawingTools.deleteAll')
  const resolvedFabTooltip = fabTooltip ?? t('drawingTools.tooltip')

  const handleToolSelect = (toolId: DrawingToolOption['id']) => {
    if (toolId === activeTool) {
      onToolSelect?.('none')
    } else {
      onToolSelect?.(toolId)
    }
  }

  return (
    <DrawingToolsContainer className={className}>
      <DrawingToolsFAB
        $active={open}
        onClick={onToggle}
        $position={fabPosition}
        $zIndex={zIndex}
        title={resolvedFabTooltip}
        aria-label={resolvedFabTooltip}
      >
        {fabContent}
      </DrawingToolsFAB>
      <DrawingToolsPanel $active={open}>
        {resolvedTools.map((tool) => (
          <DrawingToolButton
            key={tool.id}
            $active={activeTool === tool.id}
            $disabled={tool.disabled}
            onClick={() => handleToolSelect(tool.id)}
            disabled={tool.disabled}
          >
            {tool.icon}
            <span>{tool.label}</span>
          </DrawingToolButton>
        ))}
        {showDeleteAll && onDeleteAll && (
          <>
            <DrawToolsDivider />
            <DrawingToolButton $variant="delete" onClick={onDeleteAll}>
              <svg className="icon" viewBox="0 0 24 24">
                <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>{resolvedDeleteLabel}</span>
            </DrawingToolButton>
          </>
        )}
      </DrawingToolsPanel>
    </DrawingToolsContainer>
  )
}
